/**
 * Utility to detect if the current environment or route should be excluded from analytics tracking.
 * Excludes:
 * - Admin routes (/admin/*)
 * - Auth routes (/auth/*)
 * - Lovable preview/dev environments (*.lovable.app preview URLs, localhost)
 */

// Check if we're running inside the Lovable editor preview iframe
// (We only exclude when the iframe is embedded by a Lovable origin; we don't want to blanket-exclude all iframes.)
export const isLovableEditorPreview = (): boolean => {
  try {
    const isIframe = window.self !== window.top;
    if (!isIframe) return false;

    const referrer = document.referrer || '';
    if (referrer.includes('lovable')) return true;

    // Some browsers provide explicit ancestor origins
    const origins = (window.location as any).ancestorOrigins as string[] | undefined;
    if (origins && Array.from(origins).some((o) => String(o).includes('lovable'))) {
      return true;
    }

    return false;
  } catch {
    // If cross-origin frame restrictions prevent checks, be conservative and don't exclude.
    return false;
  }
};

// Check if we're in a Lovable dev/preview environment
export const isLovableDevEnvironment = (): boolean => {
  const hostname = window.location.hostname;

  // Localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }

  // Any lovable.app subdomain is dev/preview EXCEPT the published production URL
  // Production is: lt-vibe.lovable.app
  if (hostname.endsWith('.lovable.app')) {
    // Only allow the exact production lovable.app domain
    if (hostname === 'lt-vibe.lovable.app') {
      return false;
    }
    // All other lovable.app domains are dev/preview
    return true;
  }

  // Also exclude lovableproject.com preview domains
  if (hostname.endsWith('.lovableproject.com')) {
    return true;
  }

  return false;
};

// Check if the current route should be excluded from analytics
export const isExcludedRoute = (pathname: string): boolean => {
  // Admin pages
  if (pathname.startsWith('/admin')) {
    return true;
  }
  
  // Auth pages
  if (pathname.startsWith('/auth')) {
    return true;
  }
  
  return false;
};

// Combined check for analytics exclusion
export const shouldExcludeFromAnalytics = (pathname?: string): boolean => {
  // Always exclude Lovable editor iframe preview
  if (isLovableEditorPreview()) {
    return true;
  }

  // Always exclude dev environments
  if (isLovableDevEnvironment()) {
    return true;
  }

  // Check route if provided
  if (pathname && isExcludedRoute(pathname)) {
    return true;
  }

  return false;
};
